/**
 * Gerador de PDF do Laudo Técnico para Web
 * Estrutura idêntica ao Flutter: lib/core/pdf/laudo_pdf_generator.dart
 *
 * Seções:
 *  Página 1  — Capa (Seção 1 IDENTIFICAÇÃO)
 *  Módulo Detran: página REGISTROS FOTOGRÁFICOS DO VEÍCULO logo após a capa (as demais deslocam uma página)
 *  Páginas 2+ — Seções 2 OBJETIVO / 3 CONSIDERAÇÕES / 4 ANÁLISE DAS PEÇAS
 *  Última    — Seção 5 CONCLUSÃO + Assinatura + Ficha Técnica
 */
import React from "react";
import QRCode from "qrcode";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Font,
  pdf,
  Svg,
  Path,
} from "@react-pdf/renderer";
import type { LaudoWithDetails } from "@shared/schema";
import { supabase } from "./supabase";
import { carregarFotosPecasParaPdf } from "./laudo-pdf-fotos-pecas";
import { buscarParaPdf, comPrazoPdf } from "./laudo-pdf-prazo";
import {
  carregarFotosVeiculoParaPdf,
  reduzirFotoVeiculo,
  urlNaOrigem,
  type FotoVeiculoPdf,
} from "./laudo-pdf-fotos-veiculo";

// M1 (T19): o PDF só baixa imagens do armazenamento do próprio Supabase (logo, assinatura, peças e fotos do veículo).
const ORIGEM_STORAGE = `${import.meta.env.VITE_SUPABASE_URL}/storage/`;

// ─── Fontes ─────────────────────────────────────────────────────────────────
// Fontes distribuídas com o site: gerar PDF não depende de CDN externa.
Font.register({
  family: "Inter",
  fonts: [
    {
      src: "/fonts/inter-latin-400-normal.woff",
      fontWeight: 400,
    },
    {
      src: "/fonts/inter-latin-700-normal.woff",
      fontWeight: 700,
    },
  ],
});

// ─── Tema (espelho de pdf_theme.dart) ────────────────────────────────────────
const C = {
  primary: "#1E3A5F",
  accent: "#2563EB",
  textDark: "#1A1A2E",
  textMid: "#4A5568",
  textLight: "#718096",
  bgLight: "#F7F8FA",
  borderGray: "#E2E8F0",
  success: "#059669",
  warning: "#D97706",
  danger: "#DC2626",
  white: "#FFFFFF",
} as const;

const DESTINACAO_COR: Record<string, string> = {
  venda_imediata: C.success,
  recuperacao: C.warning,
  inexistente: C.danger,
  descarte: C.textLight,
};

const DESTINACAO_LABEL: Record<string, string> = {
  venda_imediata: "Venda Imediata",
  recuperacao: "Recuperação",
  inexistente: "Inexistente",
  descarte: "Descarte",
};

const VEICULO_LABEL: Record<string, string> = {
  auto_leve: "Auto / Leve",
  moto: "Moto",
  caminhao: "Caminhão",
  onibus: "Ônibus",
};

// ─── Estilos globais ─────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    fontFamily: "Inter",
    fontSize: 10,
    paddingLeft: 28,
    paddingRight: 28,
    paddingTop: 24,
    paddingBottom: 24,
    backgroundColor: C.white,
  },
  // Header
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 10,
    borderBottomWidth: 0.5,
    borderBottomColor: C.borderGray,
  },
  headerLogoText: {
    fontSize: 13,
    fontWeight: 700,
    color: C.primary,
  },
  headerCenter: { alignItems: "center" },
  headerTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: C.primary,
    letterSpacing: 1.0,
  },
  headerLaw: { fontSize: 9, color: C.textLight },
  headerRight: { alignItems: "flex-end" },
  headerLaudoId: { fontSize: 11, fontWeight: 700, color: C.primary },
  headerPage: { fontSize: 9, color: C.textLight },
  // Title box
  titleBox: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: C.primary,
    borderRadius: 4,
    paddingVertical: 14,
    paddingHorizontal: 20,
  },
  titleBoxText: {
    fontSize: 15,
    fontWeight: 700,
    color: C.white,
    letterSpacing: 0.5,
  },
  titleBadge: {
    backgroundColor: C.accent,
    borderRadius: 3,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  titleBadgeText: { fontSize: 12, fontWeight: 700, color: C.white },
  // Section title
  sectionTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: C.primary,
  },
  // Card
  card: {
    borderWidth: 0.5,
    borderColor: C.borderGray,
    borderRadius: 4,
    padding: 12,
  },
  cardTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: C.textLight,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  cardDivider: { height: 0.5, backgroundColor: C.borderGray },
  // Dado (label + value)
  datoLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: C.textLight,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  datoValue: { fontSize: 14, color: C.textDark },
  // Mini dado (para linha compacta)
  miniLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: C.textLight,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  miniValue: { fontSize: 11, fontWeight: 700, color: C.textDark },
  // Body texts
  corpo: { fontSize: 14, color: C.textDark },
  corpoLight: { fontSize: 12, color: C.textMid } as any,
  label: {
    fontSize: 11,
    fontWeight: 700,
    color: C.textLight,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  // Resumo veículo
  resumoVeiculo: {
    flexDirection: "row",
    backgroundColor: C.bgLight,
    borderWidth: 0.5,
    borderColor: C.borderGray,
    borderRadius: 4,
    padding: 8,
  },
  // Peca block
  pecaCard: {
    borderWidth: 0.5,
    borderColor: C.borderGray,
    borderRadius: 4,
  },
  pecaHeader: {
    backgroundColor: C.primary,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  pecaNumBadge: {
    width: 18,
    height: 18,
    backgroundColor: C.accent,
    borderRadius: 3,
    alignItems: "center",
    justifyContent: "center",
  },
  pecaNumText: { fontSize: 10, fontWeight: 700, color: C.white },
  pecaNome: { fontSize: 12, fontWeight: 700, color: C.white },
  destBadge: { borderRadius: 3, paddingHorizontal: 8, paddingVertical: 3 },
  destText: { fontSize: 9, fontWeight: 700, color: C.white },
  // Pergunta row
  checkBox: {
    width: 12,
    height: 12,
    borderRadius: 2,
    alignItems: "center",
    justifyContent: "center",
  },
  checkMark: { fontSize: 9, fontWeight: 700, color: C.white },
  // Summary table
  tableHeader: { backgroundColor: C.primary },
  tableHeaderCell: {
    fontSize: 10,
    fontWeight: 700,
    color: C.white,
    padding: 6,
  },
  tableCell: { fontSize: 9, color: C.textDark, padding: 6 },
  tableCellBadge: {
    borderRadius: 2,
    paddingHorizontal: 6,
    paddingVertical: 3,
  },
  // Rodapé
  rodapeContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: C.bgLight,
    borderWidth: 0.5,
    borderColor: C.borderGray,
    borderRadius: 4,
    padding: 10,
  },
  // Assinatura
  assinaturaBox: {
    width: 160,
    height: 56,
    borderWidth: 0.5,
    borderColor: C.borderGray,
  },
  assinaturaLine: { width: 160, height: 0.5, backgroundColor: C.textDark },
  assinaturaNome: { fontSize: 10, fontWeight: 700, color: C.textDark },
  assinaturaCrea: { fontSize: 9, color: C.textLight },
  // Ficha técnica elaboração
  fichaHeader: {
    backgroundColor: C.primary,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  fichaHeaderText: { fontSize: 11, fontWeight: 700, color: C.white },
  fichaContainer: {
    borderWidth: 0.5,
    borderColor: C.borderGray,
    borderRadius: 4,
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCnpj(v: string): string {
  if (!v || v.replace(/\D/g, "").length !== 14) return v || "";
  const d = v.replace(/\D/g, "");
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return "-";
  const d = new Date(date);
  return d.toLocaleDateString("pt-BR");
}

function formatDataPorExtenso(date: Date | string | null | undefined): string {
  if (!date) return "";
  const d = new Date(date);
  const months = [
    "janeiro",
    "fevereiro",
    "março",
    "abril",
    "maio",
    "junho",
    "julho",
    "agosto",
    "setembro",
    "outubro",
    "novembro",
    "dezembro",
  ];
  return `${d.getDate()} de ${months[d.getMonth()]} de ${d.getFullYear()}`;
}

async function urlToDataUrl(url: string, prazoMs: number, ladoMaximo?: number): Promise<string | null> {
  if (!urlNaOrigem(url, ORIGEM_STORAGE)) {
    throw new Error("Uma imagem do laudo está fora do armazenamento do sistema e não pode entrar no PDF. Envie a imagem novamente e tente gerar o laudo.");
  }
  try {
    const blob = await buscarParaPdf(url, {}, async (res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.blob();
    }, prazoMs, "O carregamento de uma imagem do PDF excedeu o tempo limite.");
    if (ladoMaximo) {
      return await comPrazoPdf(reduzirFotoVeiculo(blob, ladoMaximo), prazoMs, "A redução da imagem ilustrativa excedeu o tempo limite.");
    }
    return await comPrazoPdf(new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("Imagem ilegível"));
      reader.onerror = () => reject(new Error("Imagem ilegível"));
      reader.readAsDataURL(blob);
    }), prazoMs, "A leitura de uma imagem do PDF excedeu o tempo limite.");
  } catch (cause) {
    throw new Error("Não foi possível carregar uma imagem ou assinatura do laudo no tempo esperado. O PDF não foi enviado. Verifique a conexão e tente novamente.", { cause });
  }
}

// ─── Tipos de dados para o PDF ────────────────────────────────────────────────

export interface FichaTecnicaItem {
  nome: string;
  especialidade: string;
  documentoFiscal?: string | null;
}

export interface PdfData {
  laudo: LaudoWithDetails;
  fichaTecnica: FichaTecnicaItem[];
  logoDataUrl: string | null;
  assinaturaDataUrl: string | null;
  semFotoDataUrl: string | null;
  /** laudoPecaId → dataUrl da foto */
  pecaFotosDataUrls: Record<string, string | null>;
  /** Fotos cadastradas do veículo já reduzidas. Vazio = laudo sem a página. */
  fotosVeiculo: FotoVeiculoPdf[];
  qrDataUrl: string;
  docHash: string;
}

// ─── Componentes do PDF ───────────────────────────────────────────────────────

/** Cabeçalho de cada página */
const PaginaHeader: React.FC<{
  laudo: LaudoWithDetails;
  logoDataUrl: string | null;
  pageNumber: number;
  totalPages: number;
}> = ({ laudo, logoDataUrl, pageNumber, totalPages }) => (
  <View style={s.headerContainer}>
    {/* Esquerda: logo ou nome da empresa */}
    {logoDataUrl ? (
      <Image src={logoDataUrl} style={{ height: 36 }} />
    ) : (
      <Text style={s.headerLogoText}>
        {laudo.vistoriadora?.nomeFantasia ||
          laudo.vistoriadora?.razaoSocial ||
          ""}
      </Text>
    )}

    {/* Centro */}
    <View style={s.headerCenter}>
      <Text style={s.headerTitle}>LAUDO TÉCNICO MECÂNICO</Text>
      <Text style={s.headerLaw}>Lei Federal nº 12.977/2014</Text>
    </View>

    {/* Direita */}
    <View style={s.headerRight}>
      <Text style={s.headerLaudoId}>
        Nº {laudo.laudoId || laudo.id.slice(0, 8).toUpperCase()}
      </Text>
      <Text style={s.headerPage}>
        Pág. {pageNumber}/{totalPages}
      </Text>
    </View>
  </View>
);

/** Caixa de título azul */
const CaixaTitulo: React.FC<{ laudoId: string }> = ({ laudoId }) => (
  <View style={s.titleBox}>
    <Text style={s.titleBoxText}>LAUDO TÉCNICO DE VEÍCULO DESMONTADO</Text>
    <View style={s.titleBadge}>
      <Text style={s.titleBadgeText}>{laudoId}</Text>
    </View>
  </View>
);

/** Card genérico com título e linhas */
const CardDados: React.FC<{ titulo: string; children: React.ReactNode }> = ({
  titulo,
  children,
}) => (
  <View style={s.card}>
    <Text style={s.cardTitle}>{titulo}</Text>
    <View style={{ height: 8 }} />
    <View style={s.cardDivider} />
    <View style={{ height: 8 }} />
    {children}
  </View>
);

/** Campo label + valor */
const Dado: React.FC<{ label: string; valor: string }> = ({ label, valor }) => (
  <View style={{ marginBottom: 5 }}>
    <Text style={s.datoLabel}>{label}</Text>
    <View style={{ height: 2 }} />
    <Text style={s.datoValue}>{valor}</Text>
  </View>
);

/** Campo mini (compacto para rodapé/resumo) */
const MiniDado: React.FC<{ label: string; valor: string }> = ({
  label,
  valor,
}) => (
  <View style={{ flex: 1 }}>
    <Text style={s.miniLabel}>{label}</Text>
    <View style={{ height: 2 }} />
    <Text style={s.miniValue}>{valor}</Text>
  </View>
);

/** Divisor vertical */
const VDivider = () => (
  <View
    style={{
      width: 0.5,
      height: 28,
      marginHorizontal: 10,
      backgroundColor: C.borderGray,
    }}
  />
);

/** Bloco de assinatura */
const AssinaturaBloco: React.FC<{
  assinaturaDataUrl: string | null;
  rtNome: string;
  rtCrea: string;
  center?: boolean;
}> = ({ assinaturaDataUrl, rtNome, rtCrea, center }) => {
  const content = (
    <View style={{ alignItems: "center" }}>
      {assinaturaDataUrl ? (
        <Image
          src={assinaturaDataUrl}
          style={{ width: 160, height: 56, objectFit: "contain" }}
        />
      ) : (
        <View style={s.assinaturaBox} />
      )}
      <View style={{ height: 4 }} />
      <View style={s.assinaturaLine} />
      <View style={{ height: 3 }} />
      <Text style={s.assinaturaNome}>{rtNome}</Text>
      <Text style={s.assinaturaCrea}>CREA - {rtCrea}</Text>
    </View>
  );

  return center ? (
    <View style={{ alignItems: "center" }}>{content}</View>
  ) : (
    <View style={{ alignItems: "flex-end" }}>{content}</View>
  );
};

/** Linha de pergunta com checkbox */
const PerguntaRow: React.FC<{
  texto: string;
  marcado: boolean;
  observacao?: string | null;
}> = ({ texto, marcado, observacao }) => (
  <View style={{ marginBottom: 4 }}>
    <View style={{ flexDirection: "row", alignItems: "flex-start" }}>
      {/* Checkbox */}
      <View
        style={[
          s.checkBox,
          {
            backgroundColor: marcado ? C.accent : C.white,
            borderWidth: 0.8,
            borderColor: marcado ? C.accent : C.borderGray,
          },
        ]}
      >
        {marcado && (
          <Svg viewBox="0 0 10 8" style={{ width: 7, height: 6 }}>
            <Path
              d="M1 4L3.5 6.5L9 1"
              stroke={C.white}
              strokeWidth="1.8"
              fill="none"
            />
          </Svg>
        )}
      </View>
      <View style={{ width: 5 }} />
      <View style={{ flex: 1 }}>
        <Text style={s.corpoLight as any}>{texto}</Text>
      </View>
    </View>
    {marcado && observacao && observacao.trim() !== "" && (
      <View style={{ paddingLeft: 17, marginTop: 2, marginBottom: 1 }}>
        <Text
          style={{
            fontSize: 7,
            color: C.accent,
            fontWeight: 700,
          }}
        >
          OBS: {observacao}
        </Text>
      </View>
    )}
  </View>
);

/** Bloco de uma peça */
const PecaBlock: React.FC<{
  peca: NonNullable<LaudoWithDetails["laudoPecas"]>[0];
  numero: number;
  fotoDataUrl: string | null;
  semFotoDataUrl: string | null;
}> = ({ peca, numero, fotoDataUrl, semFotoDataUrl }) => {
  const respostas = peca.respostas ?? [];
  const metade = Math.ceil(respostas.length / 2);
  const colEsq = respostas.slice(0, metade);
  const colDir = respostas.slice(metade);
  const destinacao = peca.destinacao ?? "inexistente";
  const cor = DESTINACAO_COR[destinacao] || C.textLight;
  const label = DESTINACAO_LABEL[destinacao] || destinacao;

  // "outros" = respostas com outros preenchido (texto livre TV)
  const outrosTexto = respostas
    .filter((r) => r.outros?.trim())
    .map((r) => r.outros)
    .join("; ");

  return (
    <View style={s.pecaCard} wrap={false}>
      {/* Header */}
      <View style={s.pecaHeader}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <View style={s.pecaNumBadge}>
            <Text style={s.pecaNumText}>{numero}</Text>
          </View>
          <View style={{ width: 8 }} />
          <Text style={s.pecaNome}>{peca.nome}</Text>
        </View>
        <View style={[s.destBadge, { backgroundColor: cor }]}>
          <Text style={s.destText}>{label}</Text>
        </View>
      </View>

      {/* Corpo */}
      <View style={{ padding: 6, flexDirection: "row" }}>
        {/* Perguntas (flex 3) */}
        <View style={{ flex: 3 }}>
          <Text style={s.label}>ANÁLISE</Text>
          <View style={{ height: 4 }} />
          {/* 2 colunas de perguntas */}
          <View style={{ flexDirection: "row" }}>
            <View style={{ flex: 1 }}>
              {colEsq.map((r, i) => (
                <PerguntaRow
                  key={r.id ?? i}
                  texto={r.pergunta?.texto ?? ""}
                  marcado={!!r.resposta}
                  observacao={r.observacoes}
                />
              ))}
            </View>
            {colDir.length > 0 && (
              <>
                <View style={{ width: 4 }} />
                <View style={{ flex: 1 }}>
                  {colDir.map((r, i) => (
                    <PerguntaRow
                      key={r.id ?? i}
                      texto={r.pergunta?.texto ?? ""}
                      marcado={!!r.resposta}
                      observacao={r.observacoes}
                    />
                  ))}
                </View>
              </>
            )}
          </View>

          {/* Observações TV */}
          {outrosTexto && (
            <>
              <View style={{ height: 4 }} />
              <Text style={s.label}>OBSERVAÇÕES TV:</Text>
              <View style={{ height: 2 }} />
              <Text style={s.corpoLight as any}>{outrosTexto}</Text>
            </>
          )}
        </View>

        {/* Foto real ou placeholder sem_foto */}
        {(fotoDataUrl ?? semFotoDataUrl) && (
          <>
            <View style={{ width: 8 }} />
            <Image
              src={(fotoDataUrl ?? semFotoDataUrl) as string}
              style={{
                width: 140,
                height: 110,
                objectFit: "contain",
                borderRadius: 3,
              }}
            />
          </>
        )}
      </View>
    </View>
  );
};

/** Tabela resumo das peças */
const ResumoPecas: React.FC<{
  pecas: NonNullable<LaudoWithDetails["laudoPecas"]>;
}> = ({ pecas }) => (
  <View
    style={{
      borderWidth: 0.5,
      borderColor: C.borderGray,
      borderRadius: 4,
    }}
  >
    {/* Header */}
    <View style={{ flexDirection: "row", backgroundColor: C.primary }}>
      {["Seq.", "Etiqueta", "Nome da Peça", "Destinação"].map((h, i) => (
        <View
          key={h}
          style={{
            flex: i === 0 ? 0.8 : i === 1 ? 2 : i === 2 ? 3.5 : 2.7,
            borderRightWidth: i < 3 ? 0.5 : 0,
            borderRightColor: C.borderGray,
          }}
        >
          <Text style={s.tableHeaderCell}>{h}</Text>
        </View>
      ))}
    </View>
    {/* Rows */}
    {pecas.map((peca, idx) => {
      const bg = idx % 2 === 0 ? C.white : C.bgLight;
      const dest = peca.destinacao ?? "inexistente";
      const cor = DESTINACAO_COR[dest] || C.textLight;
      const destLabel = DESTINACAO_LABEL[dest] || dest;
      return (
        <View
          key={peca.id}
          style={{
            flexDirection: "row",
            backgroundColor: bg,
            borderTopWidth: 0.5,
            borderTopColor: C.borderGray,
          }}
        >
          <View
            style={{
              flex: 0.8,
              borderRightWidth: 0.5,
              borderRightColor: C.borderGray,
            }}
          >
            <Text style={s.tableCell}>{String(idx + 1).padStart(2, "0")}</Text>
          </View>
          <View
            style={{
              flex: 2,
              borderRightWidth: 0.5,
              borderRightColor: C.borderGray,
            }}
          >
            <Text style={[s.tableCell, { fontSize: 8, color: C.textLight }]}>
              {peca.etiqueta}
            </Text>
          </View>
          <View
            style={{
              flex: 3.5,
              borderRightWidth: 0.5,
              borderRightColor: C.borderGray,
            }}
          >
            <Text style={s.tableCell}>{peca.nome}</Text>
          </View>
          <View style={{ flex: 2.7, padding: 6 }}>
            <View style={[s.tableCellBadge, { backgroundColor: cor }]}>
              <Text
                style={{
                  fontSize: 8,
                  fontWeight: 700,
                  color: C.white,
                }}
              >
                {destLabel}
              </Text>
            </View>
          </View>
        </View>
      );
    })}
  </View>
);

// ─── Certificação Digital — helpers ──────────────────────────────────────────

async function computeDocHash(laudo: LaudoWithDetails): Promise<string> {
  const laudoIdDisplay = laudo.laudoId || laudo.id.slice(0, 8).toUpperCase();
  const input = [
    laudoIdDisplay,
    laudo.responsavelTecnico?.cpf ?? "",
    laudo.parceiro?.cnpj ?? "",
    laudo.dataAssinatura
      ? new Date(laudo.dataAssinatura).toISOString().slice(0, 10)
      : "",
    (laudo.laudoPecas ?? [])
      .map((p) => `${p.etiqueta}:${p.sequencia}`)
      .join("|"),
  ].join(";");
  const data = new TextEncoder().encode(input);
  const buffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
}

const CertGroup: React.FC<{ titulo: string; children: React.ReactNode }> = ({
  titulo,
  children,
}) => (
  <View>
    <Text
      style={{
        fontSize: 8,
        fontWeight: 700,
        color: C.textLight,
        letterSpacing: 0.4,
      }}
    >
      {titulo}
    </Text>
    <View style={{ height: 3 }} />
    <View style={{ height: 0.5, backgroundColor: C.borderGray }} />
    <View style={{ height: 4 }} />
    {children}
  </View>
);

const CertLinha: React.FC<{ label: string; valor: string }> = ({
  label,
  valor,
}) => (
  <View style={{ flexDirection: "row", marginBottom: 3 }}>
    <Text style={{ fontSize: 8, color: C.textLight, width: 80 }}>{label}:</Text>
    <Text style={{ fontSize: 8, fontWeight: 700, color: C.textDark, flex: 1 }}>
      {valor}
    </Text>
  </View>
);

const PaginaCertificado: React.FC<{
  laudo: LaudoWithDetails;
  logoDataUrl: string | null;
  qrDataUrl: string;
  docHash: string;
}> = ({ laudo, logoDataUrl, qrDataUrl, docHash }) => {
  const rt = laudo.responsavelTecnico;
  const laudoIdDisplay = laudo.laudoId || laudo.id.slice(0, 8).toUpperCase();
  const hashExibido = `${docHash.slice(0, 16)}...${docHash.slice(-8)}`;
  const verificationUrl = `https://laudolegal.partflow.com.br/verificar/${laudoIdDisplay}`;
  const dataEmissao = laudo.dataAssinatura
    ? new Date(laudo.dataAssinatura).toLocaleString("pt-BR")
    : new Date().toLocaleString("pt-BR");

  return (
    <Page size="A4" style={s.page}>
      <PaginaHeader
        laudo={laudo}
        logoDataUrl={logoDataUrl}
        pageNumber={1}
        totalPages={1}
      />
      <View style={{ height: 20 }} />

      {/* Título */}
      <View style={{ alignItems: "center" }}>
        <Text
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: C.primary,
            letterSpacing: 0.5,
          }}
        >
          CERTIFICAÇÃO DIGITAL DO DOCUMENTO
        </Text>
        <View style={{ height: 5 }} />
        <View style={{ width: 220, height: 2, backgroundColor: C.accent }} />
      </View>
      <View style={{ height: 10 }} />

      <Text style={{ fontSize: 9, color: C.textMid, textAlign: "justify" }}>
        Este documento foi assinado digitalmente nos termos da Medida Provisória
        nº 2.200-2/2001 e da Lei nº 14.063/2020, garantindo autenticidade e
        integridade do laudo técnico mecânico elaborado conforme a Lei Federal
        nº 12.977/2014.
      </Text>
      <View style={{ height: 14 }} />

      {/* Card principal: info + QR Code */}
      <View
        style={{
          flexDirection: "row",
          borderWidth: 0.5,
          borderColor: C.borderGray,
          borderRadius: 4,
        }}
      >
        {/* Coluna de informações */}
        <View style={{ flex: 3, padding: 14 }}>
          <CertGroup titulo="ASSINANTE">
            <CertLinha label="Nome" valor={rt?.nome ?? "-"} />
            <CertLinha label="CREA" valor={rt?.crea ?? "-"} />
            <CertLinha label="CPF" valor={rt?.cpf ?? "-"} />
          </CertGroup>
          <View style={{ height: 10 }} />

          <CertGroup titulo="CERTIFICADO ICP-BRASIL">
            <CertLinha label="Tipo" valor="ICP-Brasil A1 — Pessoa Física" />
          </CertGroup>
          <View style={{ height: 10 }} />

          <CertGroup titulo="AUTENTICIDADE DO DOCUMENTO">
            <CertLinha label="ID do Laudo" valor={laudoIdDisplay} />
            <CertLinha label="Assinado em" valor={dataEmissao} />
            <CertLinha label="Hash SHA-256" valor={hashExibido} />
          </CertGroup>
          <View style={{ height: 10 }} />

          <CertGroup titulo="VERIFICAÇÃO">
            <Text style={{ fontSize: 8, color: C.textLight }}>
              Acesse para confirmar autenticidade:
            </Text>
            <View style={{ height: 3 }} />
            <Text
              style={{
                fontSize: 8,
                color: C.accent,
                textDecoration: "underline",
              }}
            >
              {verificationUrl}
            </Text>
          </CertGroup>
        </View>

        {/* Separador vertical */}
        <View style={{ width: 0.5, backgroundColor: C.borderGray }} />

        {/* QR Code */}
        <View
          style={{
            padding: 16,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Image src={qrDataUrl} style={{ width: 110, height: 110 }} />
          <View style={{ height: 6 }} />
          <Text style={{ fontSize: 7, color: C.textLight }}>
            Escanear para verificar
          </Text>
          <Text style={{ fontSize: 7, color: C.textLight }}>autenticidade</Text>
        </View>
      </View>

      <View style={{ flex: 1 }} />

      {/* Rodapé legal */}
      <View
        style={{
          backgroundColor: C.bgLight,
          borderWidth: 0.5,
          borderColor: C.borderGray,
          borderRadius: 4,
          padding: 10,
        }}
      >
        <Text style={{ fontSize: 7, color: C.textLight, textAlign: "justify" }}>
          A validade jurídica deste documento eletrônico é garantida pela
          assinatura digital do Responsável Técnico habilitado pelo CREA, nos
          termos do Art. 8º da Lei nº 12.977/2014. A verificação de
          autenticidade pode ser realizada pelo QR Code acima ou pelo endereço
          eletrônico indicado.
        </Text>
      </View>
    </Page>
  );
};

// ─── Documento Principal ──────────────────────────────────────────────────────

const LaudoDocument: React.FC<PdfData> = ({
  laudo,
  fichaTecnica,
  logoDataUrl,
  assinaturaDataUrl,
  semFotoDataUrl,
  pecaFotosDataUrls,
  fotosVeiculo,
  qrDataUrl,
  docHash,
}) => {
  const veiculo = laudo.veiculo;
  const parceiro = laudo.parceiro;
  const vistoriadora = laudo.vistoriadora;
  const rt = laudo.responsavelTecnico;
  const tv = laudo.tecnicoVistoria;
  const pecas = laudo.laudoPecas ?? [];

  const laudoIdDisplay = laudo.laudoId || laudo.id.slice(0, 8).toUpperCase();

  const _tirarCrea = (v: string) => {
    return v.replaceAll("CREA", "").replaceAll("-", "");
  };

  return (
    <Document
      title={`Laudo Técnico ${laudoIdDisplay}`}
      author={rt?.nome}
      subject="Lei Federal nº 12.977/14"
    >
      {/* ══════════════════════════════════════════════
          PÁGINA 1 — CAPA (Seção 1: Identificação)
      ══════════════════════════════════════════════ */}
      <Page size="A4" style={s.page}>
        {/* Cabeçalho */}
        <PaginaHeader
          laudo={laudo}
          logoDataUrl={logoDataUrl}
          pageNumber={1}
          totalPages={1}
        />
        <View style={{ height: 24 }} />

        {/* Título */}
        <CaixaTitulo laudoId={laudoIdDisplay} />
        <View style={{ height: 20 }} />

        {/* Seção 1 */}
        <Text style={s.sectionTitle}>1 - IDENTIFICAÇÃO</Text>
        <View style={{ height: 12 }} />

        {/* Dois cards lado a lado */}
        <View style={{ flexDirection: "row" }}>
          <View style={{ flex: 1 }}>
            <CardDados titulo="PROPRIETÁRIO">
              <Dado
                label="Nome/Razão Social"
                valor={parceiro?.razaoSocial ?? "-"}
              />
              {parceiro?.nomeFantasia && (
                <Dado label="Nome Fantasia" valor={parceiro.nomeFantasia} />
              )}
              <Dado label="CNPJ" valor={formatCnpj(parceiro?.cnpj ?? "")} />
              <Dado label="ART" valor={laudo.artResolvida?.numero ?? "-"} />
              <Dado
                label="VIGÊNCIA ART"
                valor={formatDate(laudo.artResolvida?.vencimento ?? undefined)}
              />
            </CardDados>
          </View>
          <View style={{ width: 12 }} />
          <View style={{ flex: 1 }}>
            <CardDados titulo="ELABORAÇÃO">
              <Dado
                label="Razão Social"
                valor={vistoriadora?.razaoSocial ?? "-"}
              />
              <Dado label="CNPJ" valor={formatCnpj(vistoriadora?.cnpj ?? "")} />
              <Dado label="Responsável Técnico" valor={rt?.nome ?? "-"} />
              <Dado label="CREA" valor={_tirarCrea(rt?.crea ?? "-")} />
              <Dado label="Técnico de Vistoria" valor={tv?.nome ?? "-"} />
            </CardDados>
          </View>
        </View>
        <View style={{ height: 12 }} />

        {/* Dados do veículo */}
        <CardDados titulo="DADOS DO VEÍCULO">
          <View style={{ flexDirection: "row" }}>
            <View style={{ flex: 1 }}>
              <Dado
                label="Tipo"
                valor={
                  VEICULO_LABEL[veiculo?.tipo ?? ""] ?? veiculo?.tipo ?? "-"
                }
              />
            </View>
            <View style={{ flex: 1 }}>
              <Dado
                label="Marca"
                valor={veiculo ? `${veiculo.marca} ${veiculo.modelo}` : "-"}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Dado
                label="Ano"
                valor={String(
                  veiculo?.anoModelo || veiculo?.anoFabricacao || "-",
                )}
              />
            </View>
          </View>
          <View style={{ height: 6 }} />
          <View style={{ flexDirection: "row" }}>
            <View style={{ flex: 1 }}>
              <Dado label="Placa" valor={veiculo?.placa ?? "-"} />
            </View>
            <View style={{ flex: 1 }}>
              <Dado label="Cor" valor={veiculo?.cor ?? "-"} />
            </View>
          </View>
          <View style={{ height: 6 }} />
          <View style={{ flexDirection: "row" }}>
            <View style={{ flex: 1 }}>
              <Dado label="Chassi" valor={veiculo?.chassi ?? "-"} />
            </View>
            <View style={{ flex: 1 }}>
              <Dado label="N° Motor" valor={veiculo?.numeroMotor ?? "-"} />
            </View>
          </View>
          <View style={{ height: 6 }} />
          <View style={{ flexDirection: "row" }}>
            <View style={{ flex: 1 }}>
              <Dado label="RENAVAM" valor={veiculo?.renavam ?? "-"} />
            </View>
            <View style={{ flex: 1 }}>
              <Dado label="NF" valor={veiculo?.numeroNota ?? "-"} />
            </View>
          </View>
        </CardDados>
        <View style={{ height: 12 }} />

        {/* Rodapé da capa */}
        <View style={s.rodapeContainer}>
          <MiniDado
            label="Data da Vistoria"
            valor={formatDate(laudo.dataVistoria)}
          />
          <MiniDado
            label="Data de Assinatura"
            valor={formatDate(laudo.dataAssinatura)}
          />
          <MiniDado label="Nº do Laudo" valor={laudoIdDisplay} />
          <MiniDado label="Total de Peças" valor={String(pecas.length)} />
        </View>
      </Page>

      {/* ══════════════════════════════════════════════
          PÁGINA 2 (só laudo do módulo Detran) — REGISTROS
          FOTOGRÁFICOS DO VEÍCULO: duas colunas, até seis fotos na ordem das posições
      ══════════════════════════════════════════════ */}
      {fotosVeiculo.length > 0 && (
        <Page size="A4" style={{ ...s.page, paddingTop: 80 }}>
          <View
            fixed
            style={{ position: "absolute", top: 24, left: 28, right: 28 }}
            render={(props) => (
              <PaginaHeader
                laudo={laudo}
                logoDataUrl={logoDataUrl}
                pageNumber={props.pageNumber}
                totalPages={(props as any).totalPages ?? props.pageNumber}
              />
            )}
          />
          <CardDados titulo="REGISTROS FOTOGRÁFICOS DO VEÍCULO">
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
            >
              {fotosVeiculo.map((f) => (
                <View
                  key={f.rotulo}
                  style={{ width: "49%", marginBottom: 10 }}
                  wrap={false}
                >
                  {/* contain: página de evidência nunca corta a foto */}
                  <Image
                    src={f.dataUrl}
                    style={{
                      width: "100%",
                      height: fotosVeiculo.length > 4 ? 160 : 190,
                      objectFit: "contain",
                      backgroundColor: C.bgLight,
                    }}
                  />
                  <Text
                    style={{ fontSize: 9, marginTop: 4, textAlign: "center" }}
                  >
                    {f.rotulo}
                  </Text>
                </View>
              ))}
            </View>
          </CardDados>
        </Page>
      )}

      {/* ══════════════════════════════════════════════
          PÁGINAS 2+ — Seções 2, 3, 4 (Multi-page)
          paddingTop/Bottom na Page garante espaço em
          TODAS as páginas geradas por overflow.
      ══════════════════════════════════════════════ */}
      <Page size="A4" style={{ ...s.page, paddingTop: 80, paddingBottom: 120 }}>
        {/* Cabeçalho fixo — posicionado na zona de margem (top: 24 do papel) */}
        <View
          fixed
          style={{ position: "absolute", top: 24, left: 28, right: 28 }}
          render={(props) => (
            <PaginaHeader
              laudo={laudo}
              logoDataUrl={logoDataUrl}
              pageNumber={props.pageNumber}
              totalPages={(props as any).totalPages ?? props.pageNumber}
            />
          )}
        />

        {/* Assinatura fixa — posicionada na zona de margem inferior */}
        <View fixed style={{ position: "absolute", bottom: 24, right: 28 }}>
          <AssinaturaBloco
            assinaturaDataUrl={assinaturaDataUrl}
            rtNome={rt?.nome ?? "-"}
            rtCrea={_tirarCrea(rt?.crea ?? "-")}
            center={false}
          />
        </View>

        {/* Seção 2 */}
        <Text style={s.sectionTitle}>2 - OBJETIVO DO LAUDO</Text>
        <View style={{ height: 6 }} />
        <Text style={{ ...s.corpo, textAlign: "justify" }}>
          Laudo técnico mecânico para peças oriundas de veículos desmontados
          (carros, caminhonetes e caminhonetas), conforme a Lei Federal nº
          12.977/14, Lei do Desmonte.
        </Text>
        <View style={{ height: 14 }} />

        {/* Seção 3 */}
        <Text style={s.sectionTitle}>3 - CONSIDERAÇÕES</Text>
        <View style={{ height: 6 }} />
        <Text style={{ ...s.corpo, textAlign: "justify" }}>
          O responsável técnico não tem responsabilidade sobre procedência,
          compra ou negociação de peças ou veículos em questão, cabendo a ele
          somente análises técnicas e visuais.
        </Text>
        <View style={{ height: 14 }} />

        {/* Seção 4 */}
        <Text style={s.sectionTitle}>4 - ANÁLISE DAS PEÇAS</Text>
        <View style={{ height: 6 }} />

        {/* Mini resumo do veículo */}
        <View style={s.resumoVeiculo}>
          <MiniDado
            label="Tipo"
            valor={VEICULO_LABEL[veiculo?.tipo ?? ""] ?? veiculo?.tipo ?? "-"}
          />
          <VDivider />
          <MiniDado label="Placa" valor={veiculo?.placa ?? "-"} />
          <VDivider />
          <MiniDado label="Cor" valor={veiculo?.cor ?? "-"} />
          <VDivider />
          <MiniDado
            label="Marca/Modelo"
            valor={
              veiculo
                ? `${veiculo.marca} ${veiculo.modelo} ${veiculo.anoModelo || veiculo.anoFabricacao || ""}`
                : "-"
            }
          />
        </View>
        <View style={{ height: 12 }} />

        {/* Blocos de peças */}
        {pecas.map((peca, idx) => (
          <React.Fragment key={peca.id}>
            <PecaBlock
              peca={peca}
              numero={idx + 1}
              fotoDataUrl={pecaFotosDataUrls[peca.id] ?? null}
              semFotoDataUrl={semFotoDataUrl}
            />
            {idx < pecas.length - 1 && <View style={{ height: 6 }} />}
          </React.Fragment>
        ))}
      </Page>

      {/* ══════════════════════════════════════════════
          ÚLTIMA PÁGINA — Seção 5 Conclusão
      ══════════════════════════════════════════════ */}
      <Page size="A4" style={s.page}>
        {/* Cabeçalho */}
        <PaginaHeader
          laudo={laudo}
          logoDataUrl={logoDataUrl}
          pageNumber={1}
          totalPages={1}
        />
        <View style={{ height: 16 }} />

        {/* Seção 5 */}
        <Text style={s.sectionTitle}>5 - CONCLUSÃO</Text>
        <View style={{ height: 6 }} />
        <Text style={{ ...s.corpo, textAlign: "justify" }}>
          As peças e partes se encontram conforme descrição anterior constante
          neste documento, devendo ser destinadas conforme indicado, não
          apresentando nenhuma outra observação além das já mencionadas
          anteriormente. O estabelecimento comercial deve realizar a correta
          destinação das peças e partes, não tendo o responsável técnico
          participação ou responsabilidade no processo. As partes encaminhadas
          ao reparo deverão ser vistoriadas posteriormente e certificadas do seu
          possível uso em laudo a ser anexado a este.
        </Text>
        <View style={{ height: 12 }} />

        {/* Resumo das peças */}
        <Text style={s.sectionTitle}>RESUMO DAS PEÇAS</Text>
        <View style={{ height: 6 }} />
        <ResumoPecas pecas={pecas} />
        <View style={{ height: 8 }} />

        {/* Data por extenso */}
        <View style={{ alignItems: "center" }}>
          <Text style={{ fontSize: 12, color: C.textDark }}>
            {vistoriadora?.cidade || ""},&nbsp;
            {formatDataPorExtenso(laudo.dataAssinatura)}
          </Text>
        </View>
        <View style={{ height: 12 }} />

        {/* Assinatura centralizada */}
        <AssinaturaBloco
          assinaturaDataUrl={assinaturaDataUrl}
          rtNome={rt?.nome ?? "-"}
          rtCrea={_tirarCrea(rt?.crea ?? "-")}
          center
        />
        <View style={{ height: 8 }} />

        {/* FICHA TÉCNICA DE ELABORAÇÃO */}
        <View style={s.fichaContainer}>
          <View style={s.fichaHeader}>
            <Text style={s.fichaHeaderText}>FICHA TÉCNICA DE ELABORAÇÃO</Text>
          </View>
          <View
            style={{
              padding: 8,
              flexDirection: "row",
              justifyContent: "space-evenly",
            }}
          >
            {fichaTecnica.map((ft, i) => (
              <View key={i} style={{ marginRight: 16 }}>
                <Text
                  style={{ fontSize: 8, fontWeight: 700, color: C.textDark }}
                >
                  {ft.nome}
                </Text>
                <Text style={{ fontSize: 7, color: C.textLight }}>
                  {ft.especialidade}
                </Text>
                {ft.documentoFiscal && (
                  <Text style={{ fontSize: 7, color: C.textLight }}>
                    {ft.documentoFiscal}
                  </Text>
                )}
              </View>
            ))}
            {tv && (
              <View key={fichaTecnica.length + 1} style={{ marginRight: 16 }}>
                <Text
                  style={{ fontSize: 8, fontWeight: 700, color: C.textDark }}
                >
                  {tv.nome}
                </Text>
                <Text style={{ fontSize: 7, color: C.textLight }}>
                  Vistoriador Veicular
                </Text>
              </View>
            )}
          </View>
        </View>
      </Page>

      {/* ══════════════════════════════════════════════
          PÁGINA FINAL — Certificação Digital
          (exibida sempre que o RT tem certificado ICP-Brasil cadastrado,
          independentemente de modo de teste — ver rtTemCertificado)
      ══════════════════════════════════════════════ */}
      {laudo.rtTemCertificado && (
        <PaginaCertificado
          laudo={laudo}
          logoDataUrl={logoDataUrl}
          qrDataUrl={qrDataUrl}
          docHash={docHash}
        />
      )}
    </Document>
  );
};

// ─── Função de geração e upload ───────────────────────────────────────────────

/**
 * Gera o PDF do laudo, faz upload no Supabase Storage e retorna a URL pública.
 */
export async function generateAndUploadLaudoPdf(
  laudo: LaudoWithDetails,
  accessToken: string,
  { onProgress, prazoRecursoMs = 30_000, prazoRenderMs = 120_000, prazoUploadMs = 90_000 }:
    { onProgress?: (etapa: string) => void; prazoRecursoMs?: number; prazoRenderMs?: number; prazoUploadMs?: number } = {},
): Promise<string> {
  // 0. Fotos do veículo (módulo Detran): evidência obrigatória. Faltou, não baixou ou não
  // decodificou qualquer uma das 4 → FotosVeiculoPdfError (mensagem em português para o usuário)
  // antes de buscar o resto e de enviar qualquer arquivo. Laudos antigos incluem as fotos existentes.
  onProgress?.("Carregando fotos do veículo…");
  const fotosVeiculo = await carregarFotosVeiculoParaPdf(laudo, { origemPermitida: ORIGEM_STORAGE, timeoutMs: prazoRecursoMs });

  // 1. Buscar ficha técnica (ordenada: RT → Admin exclusivo → TV)
  let fichaTecnica: FichaTecnicaItem[] = [];
  onProgress?.("Carregando ficha técnica…");
  try {
    const raw = await buscarParaPdf("/api/ficha-tecnica", {
      headers: { Authorization: `Bearer ${accessToken}` },
    }, async (res): Promise<Array<{
        nome?: string;
        usuario?: { nome?: string; roles?: string[] };
        especialidade: string;
        documentoFiscal?: string;
      }>> => res.ok ? res.json() : [], prazoRecursoMs, "O carregamento da ficha técnica excedeu o tempo limite.");

      // Mesma lógica do Flutter: RT=0, Admin exclusivo=1, demais(TV)=2
      const rolePriority = (roles: string[] = []) => {
        if (roles.includes("rt")) return 0;
        if (roles.length === 1 && roles.includes("admin")) return 1;
        return 2;
      };
      raw.sort(
        (a, b) =>
          rolePriority(a.usuario?.roles) - rolePriority(b.usuario?.roles),
      );

      fichaTecnica = raw.map((ft) => ({
        nome: ft.nome ?? ft.usuario?.nome ?? "",
        especialidade: ft.especialidade,
        documentoFiscal: ft.documentoFiscal,
      }));
  } catch {
    // sem ficha técnica — não bloqueia geração
  }

  // 1.5 Baixar placeholder "sem foto" do bucket assets
  onProgress?.("Carregando imagens e assinatura…");
  const semFotoDataUrl = await (async () => {
    try {
      const { data: urlData } = supabase.storage
        .from("assets")
        .getPublicUrl("sem_foto/sem_foto.png");
      // Ilustração repetida nas peças sem foto: 512 px bastam para o quadro de 140 pt.
      // O PNG original de 1024 px era incorporado 47 vezes no GCDV-0003 (~13 MB).
      return urlData.publicUrl ? await urlToDataUrl(urlData.publicUrl, prazoRecursoMs, 512) : null;
    } catch {
      return null;
    }
  })();

  // 2. Baixar logo da vistoriadora (se existir)
  const logoDataUrl = laudo.vistoriadora?.logoUrl
    ? await urlToDataUrl(laudo.vistoriadora.logoUrl, prazoRecursoMs)
    : null;

  // 3. Baixar assinatura do RT (se existir)
  const assinaturaDataUrl = laudo.assinaturaUrl
    ? await urlToDataUrl(laudo.assinaturaUrl, prazoRecursoMs)
    : null;

  // 4. Decodificar fotos das peças em JPEG comum; uma foto cadastrada nunca é omitida silenciosamente.
  onProgress?.("Carregando fotos das peças…");
  const pecaFotosDataUrls = await carregarFotosPecasParaPdf(laudo.laudoPecas ?? [], { origemPermitida: ORIGEM_STORAGE, timeoutMs: prazoRecursoMs });

  // 5. Gerar hash SHA-256 e QR Code de verificação
  const docHash = await computeDocHash(laudo);
  const laudoIdDisplay = laudo.laudoId || laudo.id.slice(0, 8).toUpperCase();
  const verificationUrl = `https://laudolegal.partflow.com.br/verificar/${laudoIdDisplay}`;
  const qrDataUrl = await QRCode.toDataURL(verificationUrl, {
    errorCorrectionLevel: "H",
    width: 200,
    margin: 1,
  });

  // 6. Renderizar o PDF
  onProgress?.("Montando páginas do PDF…");
  const blob = await comPrazoPdf(pdf(
    <LaudoDocument
      laudo={laudo}
      fichaTecnica={fichaTecnica}
      logoDataUrl={logoDataUrl}
      assinaturaDataUrl={assinaturaDataUrl}
      semFotoDataUrl={semFotoDataUrl}
      pecaFotosDataUrls={pecaFotosDataUrls}
      fotosVeiculo={fotosVeiculo}
      qrDataUrl={qrDataUrl}
      docHash={docHash}
    />,
  ).toBlob(), prazoRenderMs, "A montagem das páginas do PDF excedeu o tempo limite. O arquivo não foi enviado. Tente novamente; se persistir, informe esta etapa ao suporte.");

  // 7. Upload via servidor (service role bypassa RLS)
  const cnpj = laudo.vistoriadora?.cnpj ?? "sem-cnpj";
  onProgress?.("Enviando PDF…");
  const { publicUrl } = await buscarParaPdf(
    `/api/laudos/${laudo.laudoId}/upload-pdf?cnpj=${encodeURIComponent(cnpj)}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/pdf",
        Authorization: `Bearer ${accessToken}`,
      },
      body: blob,
    }, async (uploadRes) => {
      if (!uploadRes.ok) {
        const err = await uploadRes.json().catch(() => ({ message: uploadRes.statusText }));
        throw new Error(`Erro ao enviar PDF: ${err.message}`);
      }
      return uploadRes.json() as Promise<{ publicUrl: string }>;
    }, prazoUploadMs, "O envio do PDF excedeu o tempo limite. Recarregue o laudo para conferir se o arquivo foi recebido antes de tentar novamente.",
  );
  return publicUrl;
}
